import axiosInstance from '../utils/axiosConfig';
import { TRANSPORT_MODE_OPTIONS, CURRENCY_OPTIONS, PRODUCT_CATEGORY_OPTIONS, COUNTRY_OPTIONS, MONTH_OPTIONS, INCOTERMS_OPTIONS } from '../utils/constants';
// Phase 3: Removed personioService import - using HMS APIs directly

/**
 * Service for handling delivery-related API calls
 */

/**
 * Fetches all dropdown options for delivery forms from the backend
 * Also fetches employee data from Personio API
 */
export const fetchDeliveryDropdowns = async () => {
  try {
    // Fetch basic dropdown options from the delivery API
    const response = await axiosInstance.get('/api/deliveries/dropdowns/');
    
    // Get the initial data
    const dropdownData = response.data;
    
    // Initialize employee category arrays
    dropdownData.offices = dropdownData.offices || [];
    dropdownData.traders = dropdownData.traders || [];
    dropdownData.operators = dropdownData.operators || [];
    dropdownData.all_employees = dropdownData.all_employees || []; // Initialize all_employees array
    dropdownData.currencies = dropdownData.currencies || CURRENCY_OPTIONS; // Initialize currencies with fallback
    dropdownData.product_categories = dropdownData.product_categories || PRODUCT_CATEGORY_OPTIONS; // Initialize product categories with fallback
    dropdownData.countries = dropdownData.countries || COUNTRY_OPTIONS; // Initialize countries with fallback
    dropdownData.months = dropdownData.months || MONTH_OPTIONS; // Add month options fallback
    dropdownData.incoterms = dropdownData.incoterms || INCOTERMS_OPTIONS; // Initialize incoterms with fallback
    
    // Try to fetch HMS employee categories (offices, traders, operators, all_employees)
    try {
      const hmsResponse = await axiosInstance.get('/api/hms-categories/');
      console.log('Received HMS categories:', hmsResponse.data);
      
      // Use HMS data if available
      if (hmsResponse.data.success) {
        if (hmsResponse.data.offices && hmsResponse.data.offices.length > 0) {
          dropdownData.offices = hmsResponse.data.offices;
          console.log(`Loaded ${dropdownData.offices.length} offices from HMS API`);
        }
        
        if (hmsResponse.data.traders && hmsResponse.data.traders.length > 0) {
          dropdownData.traders = hmsResponse.data.traders;
          console.log(`Loaded ${dropdownData.traders.length} traders from HMS API`);
        }
        
        if (hmsResponse.data.operators && hmsResponse.data.operators.length > 0) {
          dropdownData.operators = hmsResponse.data.operators;
          console.log(`Loaded ${dropdownData.operators.length} operators from HMS API`);
        }
        
        // Use all_employees data from HMS if available
        if (hmsResponse.data.all_employees && hmsResponse.data.all_employees.length > 0) {
          console.log('All employees data available from HMS API:', hmsResponse.data.all_employees.length, 'employees');
        
        // Final validation check for duplicates in all_employees
        const employeeIds = new Set();
        const employeeNames = new Set();
        const duplicates = [];
        
          hmsResponse.data.all_employees.forEach(emp => {
            if (employeeIds.has(emp.value) || employeeNames.has(emp.label)) {
              duplicates.push(`${emp.label} (${emp.value})`);
            } else {
              employeeIds.add(emp.value);
              employeeNames.add(emp.label);
            }
          });
          
          if (duplicates.length > 0) {
            console.warn(`Found ${duplicates.length} duplicate employees that weren't filtered:`, duplicates);
          }
          
          dropdownData.all_employees = hmsResponse.data.all_employees;
          console.log('Using all_employees from HMS API:', dropdownData.all_employees.length, 'employee records loaded');
        } else {
          console.warn('No all_employees data available from HMS API');
        }
      }
      
    } catch (hmsError) {
      console.error('Error fetching HMS categories:', hmsError);
      // HMS data fetch failed, fallback to empty arrays or defaults
    }
    
    console.log('Final dropdown data being returned:', {
      offices: dropdownData.offices.length,
      traders: dropdownData.traders.length,
      operators: dropdownData.operators.length,
      all_employees: dropdownData.all_employees.length
    });
    
    return dropdownData;
  } catch (error) {
    console.error('Error fetching delivery dropdowns:', error);
    // Return fallback values from local constants
    return {
      transport_modes: TRANSPORT_MODE_OPTIONS,
      hms_entities: [],
      operators: [],
      traders: [],
      all_employees: [], // Return empty array - don't use constants
      offices: [],
      currencies: CURRENCY_OPTIONS, // Add currency options fallback
      product_categories: PRODUCT_CATEGORY_OPTIONS, // Add product category options fallback
      countries: COUNTRY_OPTIONS, // Add country options fallback
      months: MONTH_OPTIONS, // Add month options fallback
      incoterms: INCOTERMS_OPTIONS, // Add incoterms options fallback
      // Add other fallback values as needed
    };
  }
};

/**
 * Submit delivery for review
 */
export const submitForReview = async (deliveryId) => {
  try {
    const response = await axiosInstance.post(`/api/deliveries/${deliveryId}/submit-for-review/`);
    return response.data;
  } catch (error) {
    console.error('Error submitting delivery for review:', error);
    throw error;
  }
};

/**
 * Add comment to a delivery (for reviewers)
 */
export const addComment = async (deliveryId, comment) => {
  try {
    const response = await axiosInstance.post(`/api/deliveries/${deliveryId}/add-comment/`, {
      comment
    });
    return response.data;
  } catch (error) {
    console.error('Error adding comment to delivery:', error);
    throw error;
  }
};

/**
 * Get delivery logs (audit logs and comments)
 */
export const getDeliveryLogs = async (deliveryId) => {
  try {
    const response = await axiosInstance.get(`/api/deliveries/${deliveryId}/logs/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching delivery logs:', error);
    throw error;
  }
};

/**
 * Get deliveries pending review
 */
export const getPendingReviews = async () => {
  try {
    const response = await axiosInstance.get('/api/deliveries/pending-reviews/');
    return response.data;
  } catch (error) {
    console.error('Error fetching pending reviews:', error);
    throw error;
  }
};

/**
 * Get relationship graph data for a delivery
 */
export const getDeliveryRelationships = async (deliveryId) => {
  try {
    const response = await axiosInstance.get(`/api/deliveries/${deliveryId}/get_relationships/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching delivery relationships:', error);
    throw error;
  }
};

// Export all services
const deliveryService = {
  fetchDeliveryDropdowns,
  submitForReview,
  addComment,
  getDeliveryLogs,
  getPendingReviews,
  getDeliveryRelationships,
};

export default deliveryService;